/**
 * Live network-connectivity hook.
 *
 * Consumes the `app:network-status` events emitted by axiosSetup.js plus
 * the browser's native `online`/`offline` events. Returns a coarse status
 * that the Connectivity Pill renders:
 *
 *   - 'online'        → happy path (green dot)
 *   - 'reconnecting'  → last request failed with no response (amber, spins)
 *   - 'offline'       → OS declared us fully offline (red)
 *
 * We auto-clear 'reconnecting' back to 'online' after 4 s if no further
 * failures come in — so a single blip doesn't leave the pill stuck.
 */
import { useEffect, useState } from 'react';
import { NETWORK_STATUS_EVENT } from '../utils/axiosSetup';

const RECONNECT_HOLD_MS = 4000;

export function useConnectivity() {
  const [status, setStatus] = useState(() => {
    if (typeof navigator === 'undefined') return 'online';
    return navigator.onLine ? 'online' : 'offline';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let clearTimer = null;

    const onEvt = (e) => {
      const next = e?.detail?.status;
      if (!next) return;

      if (next === 'online') {
        if (clearTimer) { clearTimeout(clearTimer); clearTimer = null; }
        setStatus('online');
        return;
      }

      if (next === 'offline') {
        if (clearTimer) { clearTimeout(clearTimer); clearTimer = null; }
        setStatus('offline');
        return;
      }

      // reconnecting — hold state briefly so a follow-up success clears it,
      // otherwise fall back to online after RECONNECT_HOLD_MS.
      setStatus((prev) => (prev === 'offline' ? 'offline' : 'reconnecting'));
      if (clearTimer) clearTimeout(clearTimer);
      clearTimer = setTimeout(() => {
        setStatus((prev) => (prev === 'reconnecting' ? 'online' : prev));
        clearTimer = null;
      }, RECONNECT_HOLD_MS);
    };

    window.addEventListener(NETWORK_STATUS_EVENT, onEvt);
    return () => {
      window.removeEventListener(NETWORK_STATUS_EVENT, onEvt);
      if (clearTimer) clearTimeout(clearTimer);
    };
  }, []);

  return status;
}
